import { MouseEvent, useEffect, useState } from 'react';
import clsx from 'clsx';

import { getDomainName, handleSpaceClick } from 'shared/lib/utils';
import { DomainBadge } from 'shared/ui/domain-badge';
import { Tooltip } from 'shared/ui/tooltip';
import { RelevantDomains } from 'shared/lib/domains';
// eslint-disable-next-line boundaries/element-types
import { useIsEntitled } from 'features/authorization';
import { TX_ASSET_BK } from 'shared/config/entitelment-constants';

import { getCurriculumUnitLink, getUnitName } from './lib';
import styles from './styles.module.scss';

type BreadcrumbsProps = {
  domain: RelevantDomains;
  subject?: string;
  lessonNumber: string;
  domainProgramLink?: string | undefined;
  lessonLink?: string;
  unitKey?: string;
  unitTitle?: string;
  withDots?: boolean;
  isShort?: boolean;
  isCustomized?: boolean;
  isCustom?: boolean;
  isTutorial?: boolean;
  isWalkthroughActive?: boolean;
  onDomainBadgeClick?: (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => Promise<void> | void;
  onBreadcrumbClick?: (
    e: MouseEvent<HTMLAnchorElement>,
    href: string,
    isUnit: boolean,
  ) => Promise<void> | void;
};

export const Breadcrumbs = ({
  domain,
  subject,
  domainProgramLink,
  unitKey,
  unitTitle,
  lessonLink,
  lessonNumber,
  isCustomized,
  isCustom,
  isTutorial,
  onBreadcrumbClick,
  onDomainBadgeClick,
  isWalkthroughActive,
  isShort = false,
  withDots = false,
}: BreadcrumbsProps) => {
  const isRegular = !isCustomized && !isCustom;

  const [domainName, setDomainName] = useState(
    getDomainName(domain, isCustom, isWalkthroughActive),
  );
  const { isAssetFound: hasTxAsset } = useIsEntitled(TX_ASSET_BK);
  const unitLink = unitKey ? getCurriculumUnitLink(unitKey) : '';
  const unitName = unitTitle ? getUnitName(unitTitle) : '';

  useEffect(() => {
    if (hasTxAsset) {
      setDomainName(domainName.replace(/\bELA(?!R)/g, 'ELAR'));
    }
  }, [domain, isCustom, isWalkthroughActive, hasTxAsset, domainName]);

  const getBreadcrumbLinkElement = (
    href: string,
    title: string,
    isUnit: boolean,
  ) => (
    <a
      href={href}
      className={clsx(
        styles.breadcrumbsLink,
        !withDots && styles.breadcrumbsItem,
      )}
      onClick={(e) => onBreadcrumbClick?.(e, href, isUnit)}
      onKeyDown={handleSpaceClick}
      onContextMenu={(e) => onBreadcrumbClick?.(e, href, isUnit)}
    >
      {title}
    </a>
  );

  const getBreadcrumb = (
    href: string,
    title: string,
    isUnit: boolean,
    withArrow = true,
  ) => {
    if (!title) return;

    const linkElement = getBreadcrumbLinkElement(href, title, isUnit);

    return (
      <>
        <span className={styles.separator}>{withArrow ? '>' : ''}</span>
        {withDots ? (
          <Tooltip title={title} childClassNames={styles.breadcrumbsItem}>
            {linkElement}
          </Tooltip>
        ) : (
          linkElement
        )}
      </>
    );
  };

  return (
    <div
      className={clsx(
        styles.breadcrumbs,
        withDots && styles.addDots,
        isShort && styles.short,
      )}
      data-testid="stream-card-breadcrumbs"
    >
      <DomainBadge
        subject={subject}
        domain={domainName}
        onClick={onDomainBadgeClick}
        onContextMenu={onDomainBadgeClick}
        programLink={domainProgramLink}
      />

      {!isCustom &&
        !isTutorial &&
        getBreadcrumb(unitLink, unitName, true, false)}

      {isRegular &&
        !isTutorial &&
        lessonLink &&
        getBreadcrumb(lessonLink, lessonNumber, false, !!unitName)}
    </div>
  );
};
